import axios, { AxiosError } from 'axios';
import { createLogger } from './logger';
import { useAuthStore } from '@stores/authStore';
import { useToastStore } from '@stores/toastStore';
import { authAdapter } from '@services/auth';

const log = createLogger('http');

// ---------------------------------------------------------------------------
// API response envelope — all 3DGS backend endpoints follow this shape.
// ---------------------------------------------------------------------------

export interface ApiResponse<T = unknown> {
  status: number; // 0 = success, non-0 = failure
  message: string;
  data: T;
  error_code?: number;
}

// ---------------------------------------------------------------------------
// Business error thrown when the backend returns status !== 0.
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public readonly code: number,
    message: string,
    public readonly errorCode?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const http = axios.create({
  // 同域请求，baseURL 为空，由 nginx 转发 /api 到后端
  baseURL: '',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// -- Request interceptor: attach token when available -----------------------

http.interceptors.request.use((config) => {
  // 已显式设置 token header 时不覆盖（如 DevTokenAdapter.init 阶段）
  if (config.headers.has('token')) return config;
  const token = useAuthStore.getState().token;
  if (token) config.headers.set('token', token);
  return config;
});

// -- Response interceptor: unwrap envelope & throw on business error ---------

http.interceptors.response.use(
  (response) => {
    const body = response.data as ApiResponse;

    if (body.status !== 0) {
      log.warn('API business error', {
        url: response.config.url,
        status: body.status,
        message: body.message,
        errorCode: body.error_code,
      });
      throw new ApiError(body.status, body.message || 'Unknown API error', body.error_code);
    }

    // Return only the `data` payload so callers don't need to unwrap.
    response.data = body.data;
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status;
    log.error('HTTP error', {
      url: error.config?.url,
      status,
      message: error.message,
    });

    // token 过期或无效，清理登录态并提示
    if (status === 401) {
      useAuthStore.getState().clearAuth();
      void authAdapter.logout();
      useToastStore.getState().addToast('登录已过期，请重新登录', 'warning');
    }

    return Promise.reject(error);
  },
);

/** 判断错误是否为指定 HTTP 状态码。封装 axios 实现细节。 */
export function isHttpError(err: unknown, statusCode: number): boolean {
  return axios.isAxiosError(err) && err.response?.status === statusCode;
}

export { http };
